import { useMemo } from 'react';

import { usePermissionsRights } from '@work4all/data/lib/hooks/use-permissions/hooks/use-permissions-rights';

import { ITableSchema } from '@work4all/models/lib/table-schema/table-schema';

import {
  ProjectPlanningColumnsProps,
  useProjectPlanningColumns,
} from '../../planning-columns/use-project-planning-columns';
import { ProjectControllingColumnHeader } from '../../project-controlling-columns/ProjectControllingColumnHeader';
import projectControllingJsonSchema from '../../schemata/project-controlling-table-schema.json';
import jsonSchema from '../../schemata/project-table-schema.json';

interface IProps {
  projectPlanningProps: Pick<
    ProjectPlanningColumnsProps,
    'onCellSelect' | 'onUtilizationClick' | 'selectedRessourceClassCell'
  >;
  useProjectControllingColumns?: boolean;
  defaultColIds?: string[];
  removedColIds?: string[];
}

const initialschema = jsonSchema as unknown as ITableSchema<never>;

export const useProjectTableSchema = (props: IProps) => {
  const {
    projectPlanningProps,
    useProjectControllingColumns,
    defaultColIds,
    removedColIds,
  } = props;

  const { canAccessErp } = usePermissionsRights();

  const projectPlanningColumns = useProjectPlanningColumns({
    ...projectPlanningProps,
  });

  const transformedPlanningSchema = useMemo(() => {
    return {
      ...projectControllingJsonSchema,
      columns: projectControllingJsonSchema.columns.map((col) => {
        return {
          ...col,

          Header: () => (
            <ProjectControllingColumnHeader
              category={col.cell.params.category}
            />
          ),
        };
      }),
    };
  }, []);

  const schema = useMemo<ITableSchema<never>>(() => {
    let columns = [
      ...initialschema.columns,
      ...(canAccessErp ? projectPlanningColumns : []),
      ...(useProjectControllingColumns
        ? transformedPlanningSchema.columns
        : []),
    ];

    if (removedColIds) {
      columns = columns.filter((col) => !removedColIds.includes(col.id));
    }

    return {
      ...initialschema,
      columns,
    } as never;
  }, [
    projectPlanningColumns,
    transformedPlanningSchema?.columns,
    useProjectControllingColumns,
    removedColIds,
    canAccessErp,
  ]);

  if (defaultColIds) {
    schema.columns.forEach((col) => {
      col.defaultHidden = !defaultColIds.includes(col.id);
    });
  }

  return schema;
};
